import React from 'react';
import {
  Box,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';

/**
 * The results table of the PR and PO Inquiry: sortable headers (the server sorts), pages of 25, 50 or 100, and a
 * row that opens its document (click or Enter). It scrolls sideways inside its frame on a narrow screen, so the
 * page itself never does.
 */
export interface InquiryColumn<R> {
  id: string;
  header: string;
  /** The server's sort key; a column without one is not sortable. */
  sortKey?: string;
  align?: 'left' | 'right' | 'center';
  minWidth?: number;
  cell: (row: R) => React.ReactNode;
}

interface Props<R> {
  ariaLabel: string;
  columns: InquiryColumn<R>[];
  rows: R[];
  getRowKey: (row: R) => string | number;
  onRowClick?: (row: R) => void;
  rowLabel?: (row: R) => string;
  sort: string;
  dir: 'asc' | 'desc';
  onSort: (key: string, dir: 'asc' | 'desc') => void;
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
  loading: boolean;
  emptyMessage: string;
}

export default function InquiryTable<R>({
  ariaLabel, columns, rows, getRowKey, onRowClick, rowLabel, sort, dir, onSort, page, pageSize, total, onPage, onPageSize, loading, emptyMessage,
}: Props<R>): React.ReactElement {
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ height: 4 }}>{loading && <LinearProgress aria-label="Loading results" />}</Box>
      <TableContainer sx={{ maxHeight: { xs: 'none', md: 'calc(100vh - 330px)' }, overflowX: 'auto' }}>
        <Table size="small" stickyHeader aria-label={ariaLabel}>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c.id}
                  align={c.align}
                  sx={{ minWidth: c.minWidth, whiteSpace: 'nowrap', fontWeight: 600 }}
                  sortDirection={c.sortKey && sort === c.sortKey ? dir : false}
                >
                  {c.sortKey ? (
                    <TableSortLabel
                      active={sort === c.sortKey}
                      direction={sort === c.sortKey ? dir : 'asc'}
                      onClick={() => onSort(c.sortKey!, sort === c.sortKey && dir === 'asc' ? 'desc' : 'asc')}
                    >
                      {c.header}
                    </TableSortLabel>
                  ) : c.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={getRowKey(row)}
                hover
                tabIndex={onRowClick ? 0 : undefined}
                aria-label={rowLabel ? rowLabel(row) : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter') onRowClick(row); } : undefined}
                sx={{ cursor: onRowClick ? 'pointer' : undefined, verticalAlign: 'top' }}
              >
                {columns.map((c) => (
                  <TableCell key={c.id} align={c.align} sx={{ minWidth: c.minWidth }}>{c.cell(row)}</TableCell>
                ))}
              </TableRow>
            ))}
            {!rows.length && !loading && (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={Math.max(0, Math.min(page - 1, Math.max(0, Math.ceil(total / pageSize) - 1)))}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_e, p) => onPage(p + 1)}
        onRowsPerPageChange={(e) => onPageSize(Number(e.target.value))}
        labelRowsPerPage="Rows"
      />
    </Paper>
  );
}
